/**
 * **Area / Editor / Region 布局树** —— 纯逻辑，零 Vue 依赖。
 *
 * 参考 Blender 的三层概念（见 `docs/layout-redesign.md` §3）：
 *
 *   Area     屏幕被切成的矩形块。可以水平 / 垂直再切。
 *   Editor   Area 里放什么（节点图 / 页面编辑器 / 大纲 / 属性 / 输出…）
 *   Region   Area **内部**的附属区（顶栏 / 左侧工具栏 / 右侧栏 / 底部条）
 *
 * ## 为什么要有这个文件
 *
 * 之前「哪些窗口开着」是宿主里的一堆布尔：
 *
 * ```ts
 * const windows = ref({
 *   minimap: true, problems: false, keys: true, theme: false,
 * })
 * ```
 *
 * 五个布尔没有几何、没有归属、没有嵌套 —— 于是：
 *   · 每个窗口的位置只能在 CSS 里手算（`.pg__side { bottom: 158px }` 那类）；
 *   · 「切分屏幕」这件事根本没有表达方式；
 *   · 同一份状态被菜单栏和左侧工具栏各写一遍，需要手写镜像。
 *
 * 换成布局树之后，上面三件事都变成**树的纯函数操作**。最关键的收益是
 * **这个文件可以被完整测到** —— 没有 DOM、没有 Vue，`verify:layout` 里
 * 每条断言都跑在真实的树结构上。分屏交互最容易出的「关掉一个 Area 之后
 * 树坏掉 / 留下空 split」这类 bug，用 DOM 测是测不出来的。
 *
 * ## 设计取舍
 *
 * · **只支持二叉切分**（Blender 也是）。`split` 节点恰好两个子节点，
 *   直觉、序列化简单、不会出现非法布局。
 * · **不允许零个 Area**：`closeArea` 关掉最后一个 Area 是 no-op。
 *   否则整个画布会变成空白，而且用户没有任何办法恢复。
 * · **ratio 夹在 [MIN_RATIO, MAX_RATIO]**：允许拖到 0 会让某个 Area
 *   变成 0 宽，用户**看不见也拖不回来** —— 这是最经典的「拖一下就废了」。
 * · **不变量检查（`assertLayout`）随结构操作一起跑**：树坏了就要在
 *   **产生它的那一步**炸，而不是等到渲染时出现莫名其妙的空白。
 */

/* ══════════════════════════════════════════════════════════════════
   类型
   ══════════════════════════════════════════════════════════════════ */

/**
 * Area 里能放什么。
 *
 * ⚠️ 新增一种编辑器时**必须同时**在 `EDITOR_META` 里补一条：
 * 「叫什么 / 快捷键是几 / 默认开哪些 Region」。渲染层和菜单都从那张表读，
 * 不允许在组件里再写一份 if-else。
 */
/*
 * ★ 契约那一半（类型 / 编辑器元信息 / 区域表 / 约束）**已向下拆到**
 * `./areaContract.ts` —— 它零依赖，是这一层的地基。
 *
 * ⚠️ 这里**继续 re-export**，既有引用一行都不用改。
 * 新增代码请直接 import `./areaContract`。
 */
export * from './areaContract'

/*
 * ⚠️ re-export **不给本文件建立局部绑定** —— 本文件自己要用的话得显式 import。
 * （`export *` 只影响别的模块看到什么。）
 */
import type {
  AreaLeaf,
  AreaNode,
  AreaSplit,
  EditorType,
  LayoutState,
  RegionKey,
  RegionState,
  SplitDir,
} from './areaContract'
import {
  ALL_REGIONS,
  EDITOR_META,
  IMPLEMENTED_EDITORS,
  defaultRegions,
  editorRegionsOf,
  resolveEditor,
} from './areaContract'

/* ══════════════════════════════════════════════════════════════════
   约束
   ══════════════════════════════════════════════════════════════════ */

/**
 * 分隔条能拖到的极限。
 *
 * ⚠️ 不给 0 / 1：那样一侧会变成 0 像素 —— 用户看不见它，也**没有办法
 * 再拖回来**（没有可抓的边）。0.12 保证两侧都还剩得下一条分隔条。
 */
export const MIN_RATIO = 0.12
export const MAX_RATIO = 0.88

export function clampRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0.5
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio))
}

/* ══════════════════════════════════════════════════════════════════
   查询（全部是纯读，不改树）
   ══════════════════════════════════════════════════════════════════ */

/** 按前序遍历拿到所有 Area（叶子）。顺序 = 视觉上的从左到右 / 从上到下 */
export function areasOf(node: AreaNode): AreaLeaf[] {
  if (node.kind === 'area') return [node]
  return [...areasOf(node.a), ...areasOf(node.b)]
}

export function areaIdsOf(node: AreaNode): string[] {
  return areasOf(node).map((a) => a.id)
}

export function findArea(node: AreaNode, id: string): AreaLeaf | null {
  for (const area of areasOf(node)) if (area.id === id) return area
  return null
}

export function findSplit(node: AreaNode, id: string): AreaSplit | null {
  if (node.kind === 'area') return null
  if (node.id === id) return node
  return findSplit(node.a, id) ?? findSplit(node.b, id)
}

/** 第一个 Area —— 用作「焦点 Area 被关掉之后回落到哪」 */
export function firstArea(node: AreaNode): AreaLeaf {
  return node.kind === 'area' ? node : firstArea(node.a)
}

/**
 * **这个窗口现在显示哪种编辑器** —— 「当前 → 默认 → 节点图」里第一个认得的。
 *
 * ⚠️ **所有渲染 / 分派 / 能力查询都必须走它**，不要直接读 `area.editor`：
 * 那一个是**意图**（可能属于一个已经卸载的包），这一个是**现状**。
 * 两者分开正是「卸载包之后窗口会回落、装回来自己就回来」的实现方式
 * （见 `editorRegistry.ts` 的 `resolveEditor` 与 `AreaLeaf.defaultEditor`）。
 */
export function editorOf(area: AreaLeaf): EditorType {
  return resolveEditor(area.editor, area.defaultEditor)
}

/** 第一个指定类型的 Area（`console` 面板要用它判断「输出窗口开着没」） */
/**
 * 某一侧现在开着哪一页（`null` = 收起）。
 *
 * ⚠️ 存的是 tab id，可能已经失效（换了编辑器 / 卸了包）—— 所以这里
 * 过一遍声明：查不到就当没开，而不是把界面切到一个不存在的页上。
 */
export function panelOfSide(area: AreaLeaf, side: 'left' | 'right'): string | null {
  const tab = area.panels?.[side] ?? null
  if (!tab) return null
  const editor = editorOf(area)
  return (EDITOR_META[editor].panels?.[side] ?? []).some((item) => item.id === tab) ? tab : null
}

/** 设某一侧的面板（`null` = 收起这一侧；**另一侧不动**） */
export function withPanelOfSide(area: AreaLeaf, side: 'left' | 'right', tab: string | null): AreaLeaf {
  const panels = { ...area.panels, [side]: tab }
  /* `null` 时不写进对象，省得存盘里堆一堆 null */
  if (tab === null) delete (panels as Record<string, unknown>)[side]
  return { ...area, panels }
}

export function findAreaByEditor(node: AreaNode, editor: EditorType): AreaLeaf | null {
  return areasOf(node).find((a) => editorOf(a) === editor) ?? null
}

export function countAreas(node: AreaNode): number {
  return areasOf(node).length
}

/* ══════════════════════════════════════════════════════════════════
   不变量
   ══════════════════════════════════════════════════════════════════ */

/**
 * 结构自检 —— 每次结构操作之后跑一遍。
 *
 * ⚠️ 为什么要在**每次操作后**检查，而不是只在反序列化时检查：
 * 分屏交互的 bug 几乎都是「某一步之后树变成了非法形状」
 * （空 split、两个 Area 用同一个 id、ratio 变 NaN）。
 * 等到渲染时表现成「某块区域莫名空白」，再往回找是哪一步就已经很难了。
 * 在这里炸，堆栈直接指向出错的那次 `splitArea` / `closeArea`。
 */
export function assertLayout(layout: LayoutState): void {
  const ids = new Set<string>()
  const seen = new Set<string>()

  const walk = (node: AreaNode): void => {
    if (seen.has(node.id)) throw new Error(`布局树里有重复节点 id：${node.id}`)
    seen.add(node.id)

    if (node.kind === 'area') {
      ids.add(node.id)
      /*
       * ⚠️ 查的是**解析之后**的那个编辑器，不是 `node.editor`：
       * 后者允许指向一个已经卸载的包贡献的类型（那是"意图"），
       * 而"**显示的**编辑器一定是认得的"这条不变量必须成立 ——
       * 它保证渲染层拿到的元信息不会是 undefined。
       */
      const shown = editorOf(node)
      if (!EDITOR_META[shown]) {
        throw new Error(
          `Area ${node.id} 没有可显示的编辑器：` +
            `当前「${node.editor}」/ 默认「${node.defaultEditor}」都不在注册表里`,
        )
      }
      for (const key of ALL_REGIONS) {
        if (typeof node.regions[key] !== 'boolean') {
          throw new Error(`Area ${node.id} 的 Region「${key}」不是布尔值`)
        }
      }
      return
    }

    if (node.dir !== 'row' && node.dir !== 'col') {
      throw new Error(`split ${node.id} 的方向非法：${node.dir}`)
    }
    if (!Number.isFinite(node.ratio)) {
      throw new Error(`split ${node.id} 的 ratio 不是有限数：${node.ratio}`)
    }
    if (node.ratio < MIN_RATIO || node.ratio > MAX_RATIO) {
      throw new Error(
        `split ${node.id} 的 ratio 越界：${node.ratio}（应在 ${MIN_RATIO}..${MAX_RATIO}）`,
      )
    }
    walk(node.a)
    walk(node.b)
  }

  walk(layout.root)

  if (ids.size === 0) throw new Error('布局树里一个 Area 都没有')
  if (!ids.has(layout.focused)) {
    throw new Error(`focused 指向了不存在的 Area：${layout.focused}`)
  }
  if (layout.maximized !== null && !ids.has(layout.maximized)) {
    throw new Error(`maximized 指向了不存在的 Area：${layout.maximized}`)
  }
}

/* ══════════════════════════════════════════════════════════════════
   构造
   ══════════════════════════════════════════════════════════════════ */

let seq = 0

/**
 * 生成新的节点 id。
 *
 * ⚠️ 用**模块内自增**而不是 `Math.random()` / `Date.now()`：
 * 测试要断言「切分之后出现了一个叫 xxx 的 Area」，随机 id 没法断言。
 * 序列化时 id 会被原样存下来，所以重开文件不会冲突（`resetIdSeq` 只给测试用）。
 */
export function nextId(prefix: string): string {
  seq += 1
  return `${prefix}-${seq}`
}

export function resetIdSeq(value = 0): void {
  seq = value
}

export function createArea(editor: EditorType, id = nextId('area')): AreaLeaf {
  /* ⚠️ 新建时「当前」和「默认」是同一个 —— 这个窗口本来就是为它开的 */
  return { kind: 'area', id, editor, defaultEditor: editor, regions: defaultRegions(editor) }
}

export function createLayout(editor: EditorType = 'graph'): LayoutState {
  const area = createArea(editor, 'area-1')
  seq = Math.max(seq, 1)
  return { root: area, maximized: null, focused: area.id }
}

/* ══════════════════════════════════════════════════════════════════
   结构操作（全部返回新对象，不改传入的树）
   ══════════════════════════════════════════════════════════════════ */

/** 替换树里某个 Area，返回新树。找不到就原样返回（不抛异常，便于链式调用） */
function mapArea(
  node: AreaNode,
  areaId: string,
  fn: (area: AreaLeaf) => AreaNode,
): AreaNode {
  if (node.kind === 'area') return node.id === areaId ? fn(node) : node
  const a = mapArea(node.a, areaId, fn)
  const b = mapArea(node.b, areaId, fn)
  return a === node.a && b === node.b ? node : { ...node, a, b }
}

/** 删掉树里某个 Area，返回新树；被删的 Area 的兄弟**顶上来** */
function removeArea(node: AreaNode, areaId: string): AreaNode | null {
  if (node.kind === 'area') return node.id === areaId ? null : node

  const a = removeArea(node.a, areaId)
  const b = removeArea(node.b, areaId)

  // 两个子节点都没了（不可能：removeArea 最多删一个），保守返回 null
  if (a === null && b === null) return null
  // 一侧被删掉 → 整条 split 被兄弟替换（树变浅一层）
  if (a === null) return b
  if (b === null) return a
  if (a === node.a && b === node.b) return node
  return { ...node, a, b }
}

/**
 * 把 `areaId` 切成两半：原 Area 留在 `a`，新 Area 放在 `b`。
 *
 * 新 Area 的编辑器默认和原 Area 相同（Blender 切出来也是同一个编辑器，
 * 因为绝大多数时候你是想「同一个图看两处」）。Region 状态也复制一份。
 */
export function splitArea(
  layout: LayoutState,
  areaId: string,
  dir: SplitDir,
  ratio = 0.5,
): LayoutState {
  const target = findArea(layout.root, areaId)
  if (!target) return layout

  const fresh = createArea(editorOf(target))
  const root = mapArea(layout.root, areaId, (area) => ({
    kind: 'split',
    id: nextId('split'),
    dir,
    ratio: clampRatio(ratio),
    a: area,
    b: { ...fresh, regions: { ...area.regions } },
  }))

  const next: LayoutState = { ...layout, root, focused: fresh.id, maximized: null }
  assertLayout(next)
  return next
}

/**
 * 关掉一个 Area，它的兄弟顶上来。
 *
 * ⚠️ 关掉**最后一个** Area 是 no-op（不是抛异常，也不是变成空树）：
 * 界面上「关闭」按钮不该在只剩一个时把整个工作区清空 —— 用户没有任何
 * 办法把它加回来，只能刷新页面。
 */
export function closeArea(layout: LayoutState, areaId: string): LayoutState {
  if (countAreas(layout.root) <= 1) return layout
  if (!findArea(layout.root, areaId)) return layout

  const root = removeArea(layout.root, areaId)
  if (!root) return layout

  const stillThere = areaIdsOf(root)
  const focused = stillThere.includes(layout.focused)
    ? layout.focused
    : firstArea(root).id
  const next: LayoutState = {
    root,
    focused,
    // 最大化中被关掉 → 自动取消最大化，否则会指着一个不存在的 Area
    maximized:
      layout.maximized && stillThere.includes(layout.maximized)
        ? layout.maximized
        : null,
  }
  assertLayout(next)
  return next
}

/**
 * 换掉某个 Area 的编辑器。Region 会按新编辑器的能力**重新收敛**。
 *
 * ⚠️ 默认**只改「当前」**（`area.editor`），不动 `defaultEditor` ——
 * 用户手动切窗口类型不该改变这个窗口的"身份"，否则
 * 「把页面编辑器窗口切成节点图、再装一次包」就会莫名其妙地变回页面编辑器。
 * 只有**新建**窗口（`revealEditor`）才传 `{ asDefault: true }`。
 */
export function setEditor(
  layout: LayoutState,
  areaId: string,
  editor: EditorType,
  options: { asDefault?: boolean } = {},
): LayoutState {
  const current = findArea(layout.root, areaId)
  if (!current) return layout
  if (current.editor === editor && (!options.asDefault || current.defaultEditor === editor)) {
    return layout
  }

  /*
   * ⚠️ 用 `editorRegionsOf`（查不到 = 谁都不支持）而不是 `EDITOR_META[...].regions`：
   * 「当前这个编辑器所属的包已经卸载了」是一个**真实会经过这里**的状态
   * （`editor` 记的是意图）。取的是 `editorOf`（解析之后那个），
   * 因为 Region 的能力表要跟**显示的那个**编辑器一致。
   */
  const prevAllowed = new Set(editorRegionsOf(editorOf(current)))
  const nextAllowed = new Set(EDITOR_META[editor].regions)
  const defaults = defaultRegions(editor)
  const regions = { ...current.regions }

  for (const key of ALL_REGIONS) {
    /*
     * ⚠️ 新编辑器**不支持**的 Region 必须强制关掉。
     * 否则会留下「大纲 Area 上挂着节点工具栏」这种状态：开关读作「开」，
     * 而渲染层因为不支持而不画 —— 用户看到一个永远关不掉的开关。
     */
    if (!nextAllowed.has(key)) {
      regions[key] = false
      continue
    }
    /*
     * 上一个编辑器不支持、现在支持了 → 当前这个 `false` 是**被能力强制**的，
     * 不是用户的选择，所以用新编辑器的默认值把它放出来。
     * 反过来，上一个编辑器就支持的 Region，值是用户的意愿，**原样保留**。
     */
    if (!prevAllowed.has(key)) regions[key] = defaults[key]
  }

  const root = mapArea(layout.root, areaId, (area) => ({
    ...area,
    editor,
    ...(options.asDefault ? { defaultEditor: editor } : {}),
    regions,
  }))
  const next: LayoutState = { ...layout, root }
  assertLayout(next)
  return next
}

/**
 * 交换两个 Area 的**内容**（编辑器 + Region 开关），**id 留在原地**。
 *
 * ## 为什么交换「内容」而不是「把两个 Area 从树上搬走」
 *
 * 两种做法看起来差不多，但代价差很多：
 *
 *   · 搬 Area 节点 → 树的结构变了（谁在左谁在右对调）→
 *     `focused` / `maximized` 指向的 id 虽然还合法，但**几何位置变了**，
 *     用户看到的是「整个窗口跳过去了」；
 *   · 交换内容 → 树**一个字节都没动**，只有两个叶子的
 *     `editor`/`defaultEditor`/`regions` 换了。`assertLayout` 的不变量
 *     天然还成立，比例、焦点、最大化全不受影响。
 *
 * 第二种也正好对上用户的话：「交换两个 Area 的**编辑器内容**」。
 *
 * ⚠️ Region 开关要**跟着一起换**。只换 `editor` 的话，
 * 「节点图（小地图开着）」和「输出（只开底栏）」互换之后会变成
 * 「输出挂着个开不了的小地图」—— 因为那个 `minimap: true` 是上一个
 * 编辑器的意愿，不是这个编辑器的。
 *
 * 同 id、或任一 id 找不到 → 原样返回（不抛，调用方不用先判）。
 */
export function swapAreas(
  layout: LayoutState,
  areaIdA: string,
  areaIdB: string,
): LayoutState {
  if (areaIdA === areaIdB) return layout
  const first = findArea(layout.root, areaIdA)
  const second = findArea(layout.root, areaIdB)
  if (!first || !second) return layout

  /*
   * 只交换「内容」——`id` 不动。两个叶子各自保留自己的 id，
   * 于是所有按 id 索引的东西（每个 Area 的视口记忆、focused、
   * maximized）都不需要跟着搬家。
   */
  const payloadOf = (area: AreaLeaf) => ({
    editor: area.editor,
    defaultEditor: area.defaultEditor,
    regions: area.regions,
  })
  const payloadA = payloadOf(first)
  const payloadB = payloadOf(second)

  let root = mapArea(layout.root, areaIdA, (area) => ({ ...area, ...payloadB }))
  root = mapArea(root, areaIdB, (area) => ({ ...area, ...payloadA }))

  const next: LayoutState = { ...layout, root }
  assertLayout(next)
  return next
}

/**
 * 分隔条拖到多近算「要合并」。
 *
 * ⚠️ 必须比 `MIN_RATIO`（0.12）**更小**：先夹取再判断的话永远进不了合并区 ——
 * 夹取之后最极端就是 0.12，而 0.12 到不了 0.06。
 * 所以判定用的是**未夹取的原始比例**（见 `NpSplitHandle`）。
 */
export const JOIN_ZONE = 0.06

/**
 * 「把分隔条拖过头」时，这一侧能不能整体合并掉。
 *
 * 返回**要被删掉的那个 Area 的 id**；不能合并时返回 `null`。
 *
 * ## 为什么只在单 Area 时才允许
 *
 * 合并的语义是「这一侧整个消失、另一侧吃掉它的空间」。如果那一侧
 * 本身还是个嵌套切分，那「删掉这一侧」就等于一口气删掉好几个 Area ——
 * 用户拖了一下边界，结果三块屏变成一块，太意外了。
 * 嵌套时退化成**普通夹取**（拖到底就是 12%），这是可预期的。
 *
 * 单 Area 时则是明确的：拖过头 = 把这块并掉。
 */
export function joinTarget(
  layout: LayoutState,
  splitId: string,
  side: 'a' | 'b',
): string | null {
  const split = findSplit(layout.root, splitId)
  if (!split) return null
  const branch = side === 'a' ? split.a : split.b
  if (branch.kind !== 'area') return null
  /*
   * ⚠️ 最后一个 Area 不能并掉 —— 和 `closeArea` 同一条底线：
   * 工作区不能变成空的（那时没有任何东西可以承载编辑器）。
   */
  if (countAreas(layout.root) <= 1) return null
  return branch.id
}

/** 拖动分隔条 */
export function setRatio(
  layout: LayoutState,
  splitId: string,
  ratio: number,
): LayoutState {
  if (!findSplit(layout.root, splitId)) return layout

  const walk = (node: AreaNode): AreaNode => {
    if (node.kind === 'area') return node
    if (node.id === splitId) return { ...node, ratio: clampRatio(ratio) }
    const a = walk(node.a)
    const b = walk(node.b)
    return a === node.a && b === node.b ? node : { ...node, a, b }
  }

  const next: LayoutState = { ...layout, root: walk(layout.root) }
  assertLayout(next)
  return next
}

/** 开关一个 Area 的某个 Region / 覆盖层 */
export function setRegion(
  layout: LayoutState,
  areaId: string,
  key: RegionKey,
  value: boolean,
): LayoutState {
  const area = findArea(layout.root, areaId)
  if (!area || area.regions[key] === value) return layout
  /*
   * ⚠️ 不支持这个 Region 的编辑器上**拒绝打开**（而不是「打开但不渲染」）。
   * 返回原对象（引用相等）让调用方能一眼看出「这次没生效」。
   *
   * ⚠️ 问的是 `editorOf(area)`（**显示的那个**编辑器）：`area.editor` 可能
   * 指向一个已经卸载的包，而"这个 Region 能不能开"问的是眼下显示的东西。
   * 它也**必须安静地不生效**，不能在这里炸掉。
   */
  if (value && !editorRegionsOf(editorOf(area)).includes(key)) return layout

  const root = mapArea(layout.root, areaId, (a) => ({
    ...a,
    regions: { ...a.regions, [key]: value },
  }))
  const next: LayoutState = { ...layout, root }
  assertLayout(next)
  return next
}

/**
 * 设某一侧的面板（`null` = 收起）。
 *
 * ⚠️ 和 `setRegion` 同一个形状：查不到 / 没变化就**返回原对象**
 * （引用相等 = 这次没生效），调用方据此判断要不要重渲染。
 */
export function setPanel(
  layout: LayoutState,
  areaId: string,
  side: 'left' | 'right',
  tab: string | null,
): LayoutState {
  const area = findArea(layout.root, areaId)
  if (!area) return layout
  if (panelOfSide(area, side) === tab) return layout
  /* ⚠️ 声明的 tab 才允许开（查不到就当作"没这一页"，安静地不生效） */
  if (tab && !(EDITOR_META[editorOf(area)].panels?.[side] ?? []).some((item) => item.id === tab)) {
    return layout
  }
  const root = mapArea(layout.root, areaId, (a) => withPanelOfSide(a, side, tab))
  const next: LayoutState = { ...layout, root }
  assertLayout(next)
  return next
}

export function toggleRegion(
  layout: LayoutState,
  areaId: string,
  key: RegionKey,
): LayoutState {
  const area = findArea(layout.root, areaId)
  if (!area) return layout
  return setRegion(layout, areaId, key, !area.regions[key])
}

/**
 * 最大化 / 还原。`null` = 还原。
 *
 * ⚠️ 只动 `maximized` 字段，**不动树** —— 所以「最大化再还原」
 * 能原样回到用户调好的分屏比例（见 `LayoutState.maximized` 的注释）。
 */
export function maximizeArea(
  layout: LayoutState,
  areaId: string | null,
): LayoutState {
  if (areaId !== null && !findArea(layout.root, areaId)) return layout
  if (layout.maximized === areaId) return layout
  return { ...layout, maximized: areaId }
}

/** 切换焦点。会顺带取消「焦点 Area 之外」的最大化吗？—— 不会，两件事独立 */
export function focusArea(layout: LayoutState, areaId: string): LayoutState {
  if (layout.focused === areaId) return layout
  if (!findArea(layout.root, areaId)) return layout
  return { ...layout, focused: areaId }
}

/** 新窗口默认贴在**哪一侧**（切分方向与宿主占比） */
export type RevealDir = 'row' | 'col'

/**
 * 按编辑器类型找 Area；找到就聚焦，**找不到就新建一个**（切分主 Area）。
 *
 * 「输出窗口」「主题编辑器窗口」都走它：如果已经有对应编辑器就跳过去，
 * 没有就切一个出来 —— 用户不用先手动分屏再选编辑器。
 *
 * ## ⚠️ 为什么方向要**传进来**，而不是写死一个
 *
 * 原来写死 `'col'`（上下分栏）。实测反馈是「添加窗口，优先添加**左右**的分割」——
 * 桌面宽屏上左右分确实更合理：编辑器列表/属性这类内容要的是**宽度**，
 * 而上下分栏会把两边都压成矮条。
 *
 * 但手机正好相反：390px 宽切左右之后每栏不到 200px，节点图基本没法看；
 * 上下切则是「宽度不变、高度减半」，仍然能用。`presets.ts` 的文件头
 * 早就把这条写下来了。
 *
 * ⚠️ 而 `areas.ts` 是**零依赖的纯函数层**（`verify-layers` 守着），
 * 它读不到视口宽度。所以方向由**宿主**决定并传进来 ——
 * 这不是把决定推给别人，而是这一层本来就不该知道屏幕多宽。
 *
 * @param dir 新窗口贴哪一侧。默认 `'row'`（左右），手机档传 `'col'`
 */
export function revealEditor(
  layout: LayoutState,
  editor: EditorType,
  dir: RevealDir = 'row',
): LayoutState {
  const existing = findAreaByEditor(layout.root, editor)
  if (existing) {
    return focusArea(maximizeArea(layout, null), existing.id)
  }
  const host = findArea(layout.root, layout.focused) ?? firstArea(layout.root)
  /*
   * ⚠️ 占比给了新窗口 **38%**（宿主 0.62）。
   * 原来 `col` 用的是 0.68 —— 那在上下分栏里是「宿主占 68% 高度」，
   * 换成左右之后同样一个 0.68 会让新窗口只剩 32% 宽（390px 屏上 125px），
   * 窄到放不下一列设置项。0.62 和内置预设「节点图 + 主题」保持一致。
   */
  const split = splitArea(layout, host.id, dir, 0.62)
  /*
   * ⚠️ `asDefault: true` —— 这是**新建**窗口，它"本来就是"这个编辑器。
   * 用户之后手动切走只改「当前」，所以这个身份会被记住
   * （卸载/重装那个包时行为才可预期，见 `AreaLeaf.defaultEditor`）。
   */
  return setEditor(split, split.focused, editor, { asDefault: true })
}

/* ══════════════════════════════════════════════════════════════════
   序列化
   ══════════════════════════════════════════════════════════════════ */

export interface LayoutDoc {
  version: 1
  root: AreaNode
  maximized: string | null
  focused: string
}

export function serializeLayout(layout: LayoutState): LayoutDoc {
  return {
    version: 1,
    root: layout.root,
    maximized: layout.maximized,
    focused: layout.focused,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * 从 JSON 还原。
 *
 * ⚠️ **绝不抛异常** —— 返回值可能是 `fallback`。
 * 布局来自用户文件 / localStorage，任何字段都可能是坏的或旧版本的。
 * 打开一个模板文件不该因为布局字段损坏就整个失败：布局丢了只是难看，
 * 打不开文件是数据丢失。
 *
 * ⚠️ id 去重：坏文件里可能有重复 id，那会让 `findArea` 只找到第一个，
 * 另一个 Area 变成永远点不到、也关不掉的幽灵。
 *
 * ⚠️ **认不出来的编辑器类型照样收下**（2027 改）：它可能属于一个
 * **没装**的包 —— 这份布局是从别人机器上拿来的，或者那个包刚被卸载。
 * 以前是「整个 Area 被丢掉」（`return null`），那会让用户的分屏**少一格**
 * 而且**没有说明**。现在按「意图 + 默认」收下：
 *   · `editor` 原样记着（装回那个包，窗口自己就回来了）；
 *   · `defaultEditor` 缺省等于 `editor`（旧文件没有这个字段）；
 *   · 显示的时候由 `editorOf()` 回落到认得的那个。
 */
export function deserializeLayout(
  doc: unknown,
  fallback: LayoutState = createLayout('graph'),
): LayoutState {
  try {
    if (!isRecord(doc)) return fallback
    if (doc.version !== 1) return fallback

    const seenIds = new Set<string>()

    const parseNode = (raw: unknown): AreaNode | null => {
      if (!isRecord(raw)) return null
      const id = typeof raw.id === 'string' && raw.id ? raw.id : null
      if (!id || seenIds.has(id)) return null
      seenIds.add(id)

      if (raw.kind === 'area') {
        const editor = typeof raw.editor === 'string' && raw.editor ? raw.editor : 'graph'
        /* ⚠️ 旧文件没有 defaultEditor —— 缺省等于 editor（它本来就是为它开的） */
        const defaultEditor =
          typeof raw.defaultEditor === 'string' && raw.defaultEditor
            ? raw.defaultEditor
            : editor
        /*
         * Region 的能力表按**显示的那个**编辑器算，而不是 `editor`：
         * 包没装时 `editor` 的能力表根本查不到（`editorRegionsOf` 给空集），
         * 那会把所有 Region 都关掉 —— 包括 header，窗口就找不回来了。
         */
        const shown = resolveEditor(editor, defaultEditor)
        const allowed = new Set(editorRegionsOf(shown))
        const regions = defaultRegions(shown)
        const rawRegions = isRecord(raw.regions) ? raw.regions : {}
        for (const key of ALL_REGIONS) {
          // 不支持的 Region 一律关掉（同 setEditor 的理由）
          if (!allowed.has(key)) {
            regions[key] = false
            continue
          }
          const value = rawRegions[key]
          if (typeof value === 'boolean') regions[key] = value
        }
        return { kind: 'area', id, editor, defaultEditor, regions }
      }

      if (raw.kind === 'split') {
        if (raw.dir !== 'row' && raw.dir !== 'col') return null
        const a = parseNode(raw.a)
        const b = parseNode(raw.b)
        if (!a || !b) return null
        return {
          kind: 'split',
          id,
          dir: raw.dir,
          ratio: clampRatio(typeof raw.ratio === 'number' ? raw.ratio : 0.5),
          a,
          b,
        }
      }
      return null
    }

    const root = parseNode(doc.root)
    if (!root) return fallback

    const ids = areaIdsOf(root)
    if (ids.length === 0) return fallback

    const maximized =
      typeof doc.maximized === 'string' && ids.includes(doc.maximized)
        ? doc.maximized
        : null
    const focused =
      typeof doc.focused === 'string' && ids.includes(doc.focused)
        ? doc.focused
        : firstArea(root).id

    const layout: LayoutState = { root, maximized, focused }
    assertLayout(layout)
    return layout
  } catch {
    return fallback
  }
}
